import { match } from 'ts-pattern'
import { inflect } from 'inflection'
import { BulkActionTitleContext } from './types'

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

/**
 * Builds an in-flight title renderer for a bulk action from its verb forms,
 * covering all singular/plural × processing/completed/failed cases. Most bulk
 * actions follow this regular shape; the optional `suffix` handles trailing
 * clauses like "as sent" or "to draft".
 */
export const bulkActionTitle =
  (spec: {
    /** Present participle, e.g. "Voiding". */
    verbing: string
    /** Past participle, e.g. "voided". */
    past: string
    /** Infinitive, e.g. "void". */
    verb: string
    /** Singular noun, e.g. "invoice". */
    noun: string
    /** Optional trailing clause, e.g. "as sent", "to draft". */
    suffix?: string
  }) =>
  (ctx: BulkActionTitleContext): string => {
    const suffix = spec.suffix ? ` ${spec.suffix}` : ''
    const nounPlural = inflect(spec.noun, 2)

    return match({ status: ctx.status, singular: ctx.singularAction })
      .with(
        { status: 'PROCESSING', singular: true },
        () => `${spec.verbing} ${spec.noun}${suffix}...`
      )
      .with(
        { status: 'PROCESSING', singular: false },
        () => `${spec.verbing} ${ctx.progress} ${nounPlural}${suffix}...`
      )
      .with(
        { status: 'COMPLETED', singular: true },
        () => `${capitalize(spec.noun)} ${spec.past}${suffix}`
      )
      .with(
        { status: 'COMPLETED', singular: false },
        () => `${ctx.totalSuccessful} ${nounPlural} ${spec.past}${suffix}`
      )
      .with(
        { status: 'COMPLETED_WITH_FAILURES', singular: true },
        () => `Failed to ${spec.verb} ${spec.noun}${suffix}`
      )
      .with(
        { status: 'COMPLETED_WITH_FAILURES', singular: false },
        () =>
          `${ctx.totalFailed} ${inflect(spec.noun, ctx.totalFailed)} failed to ${spec.verb}${suffix}`
      )
      .exhaustive()
  }

/**
 * Wraps a download bulk action's server-phase title renderer with its
 * client-side zip phases. A download keeps working after the server job is
 * terminal — it fetches and saves the zip — so entities that own a download
 * action compose this in their config, keeping all title copy config-side.
 */
export const downloadZipTitle =
  (serverPhaseTitle: (ctx: BulkActionTitleContext) => string) =>
  (ctx: BulkActionTitleContext): string =>
    match(ctx.zipStatus)
      .with('preparing', () => 'Preparing zip...')
      .with('ready', () => 'Download ready')
      .with('error', () => 'Download failed')
      .with('idle', () => serverPhaseTitle(ctx))
      .exhaustive()
